import React, { useState } from 'react';
import cn from 'classnames';

import { beMarketingEmailAnalyticsRead } from 'actions/email/beMarketingEmail.action';
import ExternalLink from 'components/generic/ExternalLink.component';
import Icon from 'components/generic/Icon.component';
import OverlaySpinner from 'components/generic/OverlaySpinner.component';
import CurrencySign from 'components/price/CurrencySign.component';
import S from 'constants/string.const';
import { usePrepareReadOnly } from 'hooks/usePrepare.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import asArray from 'utils/arrays/asArray.util';
import sortArray from 'utils/arrays/sortedArray.util';
import { formatDayToYeaCompact } from 'utils/date/format.util';
import C from 'utils/values/constant.util';

import CN from './EmailAnalytics.module.scss';

export const COLUMN = C({
  campaignSent: 'campaignSent',
  sends: 'sends',
  opens: 'opens',
  clicks: 'clicks',
  count: 'count',
  revenue: 'revenue',
  cost: 'cost',
});

const EmailAnalytics = () => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();

  const resultAnalyticsRead = usePrepareReadOnly(beMarketingEmailAnalyticsRead);
  const { qj, isLoading } = resultAnalyticsRead || {};
  const { campaigns: list, totalAppointmentsCount = 0, totalClientsCount = 0, totalRevenue = 0 } = qj || {};

  const campaigns = asArray(list);

  const [sortCampaigns, setCampaignsState] = useState(campaigns);
  const [sortOrders, setSortOrders] = useState({
    campaignSent: true, // true = asc
    sends: true,
    opens: true,
    clicks: true,
    count: true,
    revenue: true,
    cost: true,
  });

  const sortByColumn = (column) => {
    setCampaignsState(() => {
      const asc = sortOrders[column];

      const sorted = sortArray((a, b) => {
        const valA = column === COLUMN.campaignSent ? new Date(a[column]) : a[column];
        const valB = column === COLUMN.campaignSent ? new Date(b[column]) : b[column];
        return asc ? valA - valB : valB - valA;
      }, campaigns);

      setSortOrders((prevOrders) => ({
        ...prevOrders,
        [column]: !prevOrders[column],
      }));

      return sorted;
    });
  };

  return (
    <div
      data-bem="EmailAnalytics"
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      <div className={CN.container}>
        <div className={CN.title}>
          {S.chartIncreasing}
          {S.space} {t('emailAnalytics.title')}
        </div>
        <div className={CN.subTitle}>{t('emailAnalytics.description')}</div>

        {isLoading && <OverlaySpinner />}

        <div className={CN.analyticsContainer} data-bem="EmailAnalytics__container">
          <div className={CN.box} data-bem="EmailAnalytics__box-total-campaigns">
            <Icon tag="EmailAnalytics__envelope" variant="envelope" width="xl" />
            <span className={CN.boxTitle}>{t('emailAnalytics.campaignsSent')}</span>
            <div className={CN.boxValue}>{campaigns.length}</div>
          </div>

          <div className={CN.box} data-bem="EmailAnalytics__box-total-clients">
            <Icon tag="EmailAnalytics__users" variant="users" width="xl" />
            <span className={CN.boxTitle}>{t('emailAnalytics.clientsEmailed')}</span>
            <div className={CN.boxValue}>{totalClientsCount}</div>
          </div>

          <div className={CN.box} data-bem="EmailAnalytics__box-total-bookings">
            <Icon tag="EmailAnalytics__bookings" variant="calendarSolid" width="xl" />
            <span className={CN.boxTitle}>{t('emailAnalytics.bookingsReceived')}</span>
            <div className={CN.boxValue}>{totalAppointmentsCount}</div>
          </div>

          <div className={CN.box} data-bem="EmailAnalytics__box-total-revenue">
            <Icon tag="EmailAnalytics__currency" variant="currency" width="xl" />
            <span className={CN.boxTitle}>{t('emailAnalytics.revenueGenerated')}</span>
            <div className={CN.boxValue}>
              <CurrencySign value={totalRevenue} />
            </div>
          </div>
        </div>

        {/* Table  Analytics */}
        <div className={CN.panel}>
          <div className={CN.outer} data-bem="EmailAnalytics__outer">
            <div className={CN.clientsTableWrapper}>
              <div className={CN.clientsTable} data-bem="EmailAnalytics__table">
                <div data-bem="EmailAnalytics__table--header" className={CN.tableHeader}>
                  <div className={CN.sell} onClick={() => sortByColumn(COLUMN.campaignSent)}>
                    {t('emailAnalytics.tableHeaders.dateSent')} <Icon variant="arrowTriangle" width="xs" />
                  </div>
                  <div className={CN.sell}>{t('emailAnalytics.tableHeaders.campaignName')}</div>
                  <div className={CN.sell} onClick={() => sortByColumn(COLUMN.sends)}>
                    {t('emailAnalytics.tableHeaders.sent')} <Icon variant="arrowTriangle" width="xs" />
                  </div>
                  <div className={CN.sell} onClick={() => sortByColumn(COLUMN.opens)}>
                    {t('emailAnalytics.tableHeaders.opened')} <Icon variant="arrowTriangle" width="xs" />
                  </div>
                  <div className={CN.sell} onClick={() => sortByColumn(COLUMN.clicks)}>
                    {t('emailAnalytics.tableHeaders.clicked')} <Icon variant="arrowTriangle" width="xs" />
                  </div>
                  <div className={CN.sell} onClick={() => sortByColumn(COLUMN.count)}>
                    {t('emailAnalytics.tableHeaders.bookings')} <Icon variant="arrowTriangle" width="xs" />
                  </div>
                  <div className={CN.sell} onClick={() => sortByColumn(COLUMN.revenue)}>
                    {t('emailAnalytics.tableHeaders.revenue')} <Icon variant="arrowTriangle" width="xs" />
                  </div>
                  <div className={CN.sell} onClick={() => sortByColumn(COLUMN.cost)}>
                    {t('emailAnalytics.tableHeaders.cost')} <Icon variant="arrowTriangle" width="xs" />
                  </div>
                </div>

                <div className={CN.tableBody} data-bem="EmailAnalytics__table--body">
                  {campaigns.length === 0 && (
                    <div className={CN.noData}>
                      <div className={CN.title}>
                        {t('emailAnalytics.noCampaignsMsg')} {S.faceWithTear}
                      </div>
                    </div>
                  )}

                  {(sortCampaigns.length > 0 ? sortCampaigns : campaigns).map((item) => (
                    <div className={CN.tableRow} key={item.id} data-bem="EmailAnalytics__table--row">
                      <div className={CN.sell} data-bem="EmailAnalytics__cell-data">
                        {/* TODO: date localisation */}
                        {formatDayToYeaCompact(item.campaignSent)}
                      </div>
                      <div className={CN.sell} data-bem="EmailAnalytics__cell-link">
                        <ExternalLink variant="inline" link={item.campaignUrl}>
                          {item.name}
                        </ExternalLink>
                      </div>
                      <div className={CN.sell} data-bem="EmailAnalytics__cell-sent">
                        {item.sends}
                      </div>
                      <div className={CN.sell} data-bem="EmailAnalytics__cell-opens">
                        {item.opens}
                      </div>
                      <div className={CN.sell} data-bem="EmailAnalytics__cell-clicked">
                        {item.clicks}
                      </div>
                      <div className={CN.sell} data-bem="EmailAnalytics__cell-bookings">
                        {item.count}
                      </div>
                      <div className={CN.sell} data-bem="EmailAnalytics__cell-revenue">
                        <CurrencySign value={item.revenue} />
                      </div>
                      <div className={CN.sell} data-bem="EmailAnalytics__cell-cost">
                        <CurrencySign value={item.cost} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailAnalytics;
